import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  BarChart3, 
  Clock, 
  Users, 
  Shield, 
  Star, 
  Heart,
  TrendingUp,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { usePerformanceEvaluations, usePerformanceDetails } from '@/hooks/usePerformanceEvaluations';
import { format } from 'date-fns';

interface PerformanceEvaluationDetailProps {
  evaluationId: string;
}

const getGradeColor = (grade: string) => {
  switch (grade) {
    case 'A+':
    case 'A':
      return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800';
    case 'B+':
    case 'B':
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800';
    case 'C+':
    case 'C':
      return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800';
    case 'D':
      return 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 border-orange-200 dark:border-orange-800';
    case 'F':
      return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
};

const ScoreCard: React.FC<{
  title: string;
  score: number;
  maxScore: number;
  icon: React.ReactNode;
  description: string;
  details?: any;
}> = ({ title, score, maxScore, icon, description, details }) => {
  const percentage = maxScore > 0 ? (score / maxScore) * 100 : 0;
  
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm">
          {icon}
          {title}
        </CardTitle>
        <CardDescription className="text-xs">{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-2xl font-bold text-foreground">{score.toFixed(1)}</span>
            <span className="text-sm text-muted-foreground">/ {maxScore}</span>
          </div>
          <Progress value={percentage} className="h-2" />
          <div className="text-xs text-muted-foreground">
            {percentage.toFixed(1)}% απόδοση
          </div>
          {details && (
            <div className="text-xs space-y-1">
              {Object.entries(details).map(([key, value]) => (
                <div key={key} className="flex justify-between">
                  <span className="capitalize text-muted-foreground">{key.replace(/_/g, ' ')}:</span>
                  <span className="font-medium text-foreground">{String(value)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export const PerformanceEvaluationDetail: React.FC<PerformanceEvaluationDetailProps> = ({ 
  evaluationId 
}) => {
  // Fetch specific evaluation
  const { data: evaluations } = usePerformanceEvaluations();
  const evaluation = evaluations?.find(e => e.id === evaluationId);

  // Fetch performance details
  const { data: performanceDetails, isLoading: detailsLoading } = usePerformanceDetails(evaluationId);

  // Fetch behavior records for this evaluation period
  const { data: behaviorRecords = [] } = useQuery({
    queryKey: ['behavior-records', evaluation?.guard_id, evaluation?.evaluation_period_start],
    queryFn: async () => {
      if (!evaluation) return [];
      
      const { data, error } = await supabase
        .from('behavior_records')
        .select(`
          *,
          behavior_type:behavior_types!behavior_type_id(*)
        `)
        .eq('guard_id', evaluation.guard_id)
        .gte('incident_date', evaluation.evaluation_period_start)
        .lte('incident_date', evaluation.evaluation_period_end)
        .eq('status', 'active');

      if (error) throw error;
      return data || [];
    },
    enabled: !!evaluation,
  });

  if (!evaluation) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-muted-foreground">Δεν βρέθηκε η αξιολόγηση</p>
        </CardContent>
      </Card>
    );
  }

  const guardName = evaluation.guard?.full_name || 
    `${evaluation.guard?.first_name} ${evaluation.guard?.last_name}`;

  const positiveBehs = behaviorRecords.filter(b => b.behavior_type?.category === 'positive');
  const negativeBehs = behaviorRecords.filter(b => b.behavior_type?.category === 'negative');

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16">
                <AvatarFallback className="text-lg">
                  {evaluation.guard?.first_name?.[0]}{evaluation.guard?.last_name?.[0]}
                </AvatarFallback>
              </Avatar>
              <div>
                <CardTitle className="text-2xl">{guardName}</CardTitle>
                <CardDescription className="text-base">
                  Περίοδος Αξιολόγησης: {format(new Date(evaluation.evaluation_period_start), 'dd/MM/yyyy')} - 
                  {format(new Date(evaluation.evaluation_period_end), 'dd/MM/yyyy')}
                </CardDescription>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold mb-2">{evaluation.weighted_score.toFixed(1)}%</div>
              <Badge className={`${getGradeColor(evaluation.performance_grade)} text-lg px-4 py-2`}>
                {evaluation.performance_grade}
              </Badge>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Performance Metrics Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <ScoreCard
          title="Απόδοση Περιπολιών"
          score={evaluation.patrol_performance_score}
          maxScore={20}
          icon={<Shield className="h-4 w-4" />}
          description="Αξιολόγηση περιπολιών και checkpoints"
          details={performanceDetails?.find(d => d.metric_type === 'patrol_performance')?.calculation_details}
        />
        
        <ScoreCard
          title="Αναφορές Επόπτη"
          score={evaluation.supervisor_reports_score}
          maxScore={15}
          icon={<Users className="h-4 w-4" />}
          description="Αξιολόγηση από αναφορές επόπτη"
          details={performanceDetails?.find(d => d.metric_type === 'supervisor_reports')?.calculation_details}
        />
        
        <ScoreCard
          title="Συνέπεια & Παρουσία"
          score={evaluation.punctuality_score}
          maxScore={15}
          icon={<Clock className="h-4 w-4" />}
          description="Παρουσία και έγκαιρη άφιξη"
          details={performanceDetails?.find(d => d.metric_type === 'punctuality')?.calculation_details}
        />
        
        <ScoreCard
          title="Αποτελέσματα Αξιολογήσεων"
          score={evaluation.assessment_score}
          maxScore={20}
          icon={<BarChart3 className="h-4 w-4" />}
          description="Βαθμολογία σε εξετάσεις και αξιολογήσεις"
          details={performanceDetails?.find(d => d.metric_type === 'assessment_results')?.calculation_details}
        />
        
        <ScoreCard
          title="Αξιολόγηση Πελατών"
          score={evaluation.client_evaluation_score}
          maxScore={15}
          icon={<Star className="h-4 w-4" />}
          description="Ικανοποίηση πελατών"
        />
        
        <ScoreCard
          title="Συμπεριφορές"
          score={evaluation.behavior_score}
          maxScore={15}
          icon={<Heart className="h-4 w-4" />}
          description="Θετικές και αρνητικές συμπεριφορές"
        />
      </div>

      {/* Performance Details */}
      {detailsLoading ? (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          </CardContent>
        </Card>
      ) : performanceDetails && performanceDetails.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Αναλυτικές Μετρικές
            </CardTitle>
            <CardDescription>
              Λεπτομερής ανάλυση των επιμέρους μετρικών απόδοσης
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {performanceDetails.map((detail) => (
                <div key={detail.id} className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-foreground">{detail.metric_name}</h4>
              <div className="text-right">
                <span className="text-lg font-bold text-foreground">{detail.points_awarded}</span>
                <span className="text-sm text-muted-foreground">/{detail.max_possible_points}</span>
              </div>
            </div>
                  
                  {detail.raw_value !== null && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Πραγματική Αξία:</span>
                        <div className="font-medium text-foreground">{detail.raw_value}</div>
                      </div>
                      {detail.baseline_value && (
                        <div>
                          <span className="text-muted-foreground">Baseline:</span>
                          <div className="font-medium text-foreground">{detail.baseline_value}</div>
                        </div>
                      )}
                      {detail.target_value && (
                        <div>
                          <span className="text-muted-foreground">Στόχος:</span>
                          <div className="font-medium text-foreground">{detail.target_value}</div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {detail.performance_notes && (
                    <div className="mt-2 text-sm text-muted-foreground">
                      <strong>Σημειώσεις:</strong> {detail.performance_notes}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Behavior Records */}
      {behaviorRecords.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-400">
                <CheckCircle className="h-5 w-5" />
                Θετικές Συμπεριφορές ({positiveBehs.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {positiveBehs.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">Δεν υπάρχουν θετικές συμπεριφορές</p>
              ) : (
                <div className="space-y-3">
                  {positiveBehs.map((record) => (
                    <div key={record.id} className="flex items-center justify-between p-3 bg-green-50/80 dark:bg-green-900/20 rounded-lg">
                      <div>
                        <div className="font-medium text-sm text-foreground">{record.behavior_type?.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {format(new Date(record.incident_date), 'dd/MM/yyyy')}
                        </div>
                      </div>
                      <div className="text-green-700 dark:text-green-400 font-bold">
                        +{record.score_applied}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
                <XCircle className="h-5 w-5" />
                Αρνητικές Συμπεριφορές ({negativeBehs.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {negativeBehs.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">Δεν υπάρχουν αρνητικές συμπεριφορές</p>
              ) : (
                <div className="space-y-3">
                  {negativeBehs.map((record) => (
                    <div key={record.id} className="flex items-center justify-between p-3 bg-red-50/80 dark:bg-red-900/20 rounded-lg">
                      <div>
                        <div className="font-medium text-sm text-foreground">{record.behavior_type?.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {format(new Date(record.incident_date), 'dd/MM/yyyy')}
                        </div>
                      </div>
                      <div className="text-red-700 dark:text-red-400 font-bold">
                        {record.score_applied}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Evaluation Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Περίληψη Αξιολόγησης
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Συνολική Raw Βαθμολογία:</span>
                <span className="font-bold text-foreground">{evaluation.total_raw_score.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Weighted Score:</span>
                <span className="font-bold text-foreground">{evaluation.weighted_score.toFixed(2)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Βαθμός Απόδοσης:</span>
                <Badge className={getGradeColor(evaluation.performance_grade)}>
                  {evaluation.performance_grade}
                </Badge>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Κατάσταση:</span>
                <span className="font-medium text-foreground">{evaluation.evaluation_status}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Ημερομηνία Δημιουργίας:</span>
                <span className="text-foreground">{format(new Date(evaluation.created_at), 'dd/MM/yyyy HH:mm')}</span>
              </div>
              {evaluation.notes && (
                <div>
                  <span className="text-sm text-muted-foreground">Σημειώσεις:</span>
                  <p className="text-sm mt-1 text-foreground">{evaluation.notes}</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};